/**
 * AI-extracted incident itinerary shapes + delta computation.
 * Port of apps/admin-dashboard/src/utils/incident-delta.ts.
 */

import { Duration } from "luxon";

/** Human-readable delta ("5 heures, 56 minutes"), sign dropped. */
export function formatDeltaMinutes(deltaInMinutes: number): string {
  const duration = Duration.fromObject({ minutes: Math.abs(deltaInMinutes) }, { locale: "fr" }).shiftTo(
    "hours",
    "minutes",
  );
  return duration.toHuman({ unitDisplay: "long" });
}

/** Signed variant ("+ 14 heures, 10 minutes" / "- 1 heure, 15 minutes") — the sign makes a delay
 * and an advance unmistakable at a glance. */
export function formatSignedDeltaMinutes(deltaInMinutes: number): string {
  const sign = deltaInMinutes > 0 ? "+ " : deltaInMinutes < 0 ? "- " : "";
  return `${sign}${formatDeltaMinutes(deltaInMinutes)}`;
}

export interface FlightDelta {
  departure?: number;
  arrival?: number;
}

/** Times read by the delta computation (local variants win when present). */
export interface FlightDeltaTimes {
  actualDeparture?: string | null;
  actualDepartureLocal?: string | null;
  scheduledDeparture?: string | null;
  scheduledDepartureLocal?: string | null;
  actualArrival?: string | null;
  inGateArrivalLocal?: string | null;
  scheduledArrival?: string | null;
  scheduledArrivalLocal?: string | null;
}

/**
 * Live monitoring of the detected flight, read from the claim itself — used when the insight
 * snapshot predates the monitoring capture (the generation raced the flight-monitoring update).
 * Times/delays are carried in UTC; the flight's *Local columns are not trusted here.
 */
export interface LiveFlightMonitoring {
  status: string;
  flightIdentifier?: string | null;
  scheduledDepartureUTC?: string | null;
  scheduledArrivalUTC?: string | null;
  actualDepartureUTC?: string | null;
  inGateArrivalUTC?: string | null;
  arrivalDelayMinutes: number | null;
  departureDelayMinutes: number | null;
}

/** "Retardé de 3 heures, 16 minutes" / "Annulé" / "Dérouté" — French label of a live status. */
export function liveMonitoringLabel(live: LiveFlightMonitoring): string {
  if (live.status === "CANCELLED") {
    return "Annulé";
  }
  if (live.status === "DIVERTED") {
    return "Dérouté";
  }
  if (live.status === "DELAYED") {
    return live.arrivalDelayMinutes != null
      ? `Retardé de ${formatDeltaMinutes(live.arrivalDelayMinutes)}`
      : "Retardé";
  }
  return live.status;
}

/** One entry of an AI-extracted itinerary (`{ segment, flight, sequence, order, isImpactedSequence }`). */
export interface AiFlightEntry {
  sequence: number;
  order: number;
  isImpactedSequence?: boolean;
  segment?: {
    segmentUuid?: string;
    textInformation?: string;
    cancellationType?: "SEGMENT" | "RECORD" | null;
    cancellationDate?: string | null;
  } | null;
  flight?:
    | (FlightDeltaTimes & {
        flightIdentifier?: string;
        departureAirport?: string;
        departureAirportName?: string | null;
        departureAirportCity?: string | null;
        arrivalAirport?: string;
        arrivalAirportName?: string | null;
        arrivalAirportCity?: string | null;
        monitoringStatus?: string | null;
        /** String in the assessment payload, `{ name }` object in the stored V3 INCIDENT insight. */
        operatingAirline?: string | { name?: string | null } | null;
        operatingAirlineName?: string | null;
        marketingAirline?: string | { name?: string | null } | null;
        marketingAirlineName?: string | null;
      })
    | null;
  delta?: FlightDelta | null;
}

export interface AiEticket {
  eticket?: string;
  passenger?: string | null;
  amadeusRecordNumber?: string | number | null;
}

/** The `AiIncidentPreview` incident prop shape (also the stored INCIDENT insight metadata). */
export interface AiIncidentData {
  incident: {
    itinerary: {
      original: AiFlightEntry[];
      actual: AiFlightEntry[];
    };
    notes?: string | null;
    cause?: string;
    consequence?: string;
  };
  etickets: AiEticket[];
}

export function getSequenceLabel(sequenceNum: number): string {
  return sequenceNum === 1 ? "Aller" : "Retour";
}

/** Groups flight entries by their `sequence`, sorted ascending. */
export function groupBySequence(flights: AiFlightEntry[]): Array<[number, AiFlightEntry[]]> {
  const groups = new Map<number, AiFlightEntry[]>();
  for (const flight of flights) {
    const group = groups.get(flight.sequence) ?? [];
    group.push(flight);
    groups.set(flight.sequence, group);
  }
  return [...groups.entries()].sort(([a], [b]) => a - b);
}

/**
 * Calculate delta between actual and original flight times
 */
export function calculateFlightDelta(
  actualFlight: AiFlightEntry,
  originalFlight: AiFlightEntry,
): FlightDelta | null {
  if (!actualFlight?.flight || !originalFlight?.flight) {
    return null;
  }

  const delta: FlightDelta = {};

  // Delta departure: actual - original scheduled. Same scheduled fallback as the arrival below —
  // a rerouted/rescheduled flight without real times still carries its *new planned* departure,
  // which is what proves a "vol avancé" (e.g. rebooked 13:15 → 12:00).
  const actualDep =
    actualFlight.flight.actualDepartureLocal ||
    actualFlight.flight.actualDeparture ||
    actualFlight.flight.scheduledDepartureLocal ||
    actualFlight.flight.scheduledDeparture;
  const origDep = originalFlight.flight.scheduledDepartureLocal || originalFlight.flight.scheduledDeparture;

  if (actualDep && origDep) {
    delta.departure = Math.round((new Date(actualDep).getTime() - new Date(origDep).getTime()) / (1000 * 60));
  }

  // Delta arrival: actual - original scheduled. Prefer the real arrival, but fall back to the actual
  // flight's *scheduled* arrival when no real time exists — otherwise a rupture de correspondance
  // (rerouted final leg, often without a real arrival yet) would show no "arrivée à destination
  // finale" delta at all. Real times still win when present.
  const actualArr =
    actualFlight.flight.inGateArrivalLocal ||
    actualFlight.flight.actualArrival ||
    actualFlight.flight.scheduledArrivalLocal ||
    actualFlight.flight.scheduledArrival;
  const origArr = originalFlight.flight.scheduledArrivalLocal || originalFlight.flight.scheduledArrival;

  if (actualArr && origArr) {
    delta.arrival = Math.round((new Date(actualArr).getTime() - new Date(origArr).getTime()) / (1000 * 60));
  }

  return Object.keys(delta).length > 0 ? delta : null;
}

/**
 * Beyond this, an actual arrival's *date* is treated as corrupt. Some assessment-pipeline insights
 * report an arrival days off (observed on claim 2be38242: a 1-min-early arrival reported ~6 days late
 * → Δ +8639min → "Retard 143h", "Correspondance −142h"). Aligned to the pipeline's own suspicion
 * threshold, which already marks such deltas `[SUSPECT: out_of_bounds_gt_72h]`.
 */
export const SANE_DELAY_CAP_MIN = 72 * 60;

export interface ArrivalReliability {
  /** Arrival delay in minutes vs schedule; 0 when unavailable or unreliable. */
  delayMinutes: number;
  /** False only when a real arrival exists but is implausible (corrupt date). */
  reliable: boolean;
  /** The real arrival timestamp to use downstream, or null when suspect/absent. */
  reliableActualArrival: string | null;
}

/**
 * Reliability of a leg's real arrival, from `actualArrival` vs `scheduledArrival`. Central guard so
 * the delay line, the correspondence maths, and the "à vérifier" state all agree (TECH-705).
 */
export function arrivalReliability(flight: FlightDeltaTimes | null | undefined): ArrivalReliability {
  const actualArrival = flight?.actualArrival;
  const scheduledArrival = flight?.scheduledArrival;
  const rawDelayMinutes =
    actualArrival && scheduledArrival
      ? Math.round((new Date(actualArrival).getTime() - new Date(scheduledArrival).getTime()) / (1000 * 60))
      : 0;
  const reliable = Math.abs(rawDelayMinutes) <= SANE_DELAY_CAP_MIN;
  return {
    delayMinutes: reliable ? rawDelayMinutes : 0,
    reliable,
    reliableActualArrival: reliable ? (actualArrival ?? null) : null,
  };
}

/**
 * "Arrivée à destination finale" delta (minutes): the actual *last* leg vs the original *last* leg,
 * so a rupture de correspondance (rerouted last leg that doesn't order-match an original) still
 * yields a delta. Real arrival first, scheduled fallback (see `calculateFlightDelta`).
 *
 * Returns null when the delta exceeds `SANE_DELAY_CAP_MIN` — same corrupt-`actualArrival` guard as
 * `arrivalReliability`, so a bad real arrival can't leak an absurd "Delta arrivée : 143 heures".
 * Schedule-fallback deltas (the rupture-de-correspondance case) stay small, so they're never hidden.
 */
export function finalArrivalDeltaMinutes(
  actualLegs: AiFlightEntry[],
  originalLegs: AiFlightEntry[],
): number | null {
  const lastActual = actualLegs[actualLegs.length - 1];
  const lastOriginal = originalLegs[originalLegs.length - 1];
  if (!lastActual || !lastOriginal) {
    return null;
  }
  // Only meaningful when the passenger actually reached the planned final destination — an
  // interrupted journey (actual itinerary stops at the connection airport) must not compare
  // arrivals at two different airports (NBO vs ZNZ read as "-6h, arrivée avancée").
  if (
    lastActual.flight?.arrivalAirport &&
    lastOriginal.flight?.arrivalAirport &&
    lastActual.flight.arrivalAirport !== lastOriginal.flight.arrivalAirport
  ) {
    return null;
  }
  const arrival = calculateFlightDelta(lastActual, lastOriginal)?.arrival ?? null;
  return arrival != null && Math.abs(arrival) > SANE_DELAY_CAP_MIN ? null : arrival;
}
